import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Dropdown from 'components/form/Dropdown';
import TextInput from 'components/form/TextInput';
import CheckBox from 'components/form/CheckBox';
import Button from 'components/form/Button';
import Loader from 'components/Layout/Loader';
import TextArea from 'components/form/TextArea';
import PaymentPopup from 'pages/Common/InstantTicketGenerate/PaymentPopup';
import PreviewReceiptPopup from 'pages/Common/InstantTicketGenerate/PreviewReceiptPopup';
import PreviewHallTicketPopup from 'pages/Common/InstantTicketGenerate/PreviewHallTicketPopup';
import {
  currencyType,
  icons,
  paymentCategory,
  paymentType,
  profilePath,
} from 'utils/constants';
import {
  fetchAbstractDetails,
  handleActionOnPaper,
  showSuccess,
} from 'store/slices';
import {
  generatePreSignedUrl,
  getDataFromLocalStorage,
  getUserType,
  objectToFormData,
  paymentColor,
} from 'utils/helpers';
import Receipt from './Receipt';
import './ClientConferenceTicketsView.scss';
import Card from 'components/Layout/Card';
import AccountantVerifyData from './AccountantVerifyData';

const ClientConferenceTicketsView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { abstract_id, eventId } = params;
  const [coAuthorName, setCoAuthorName] = useState(true);
  const [openPaymentPopup, setOpenPaymentPopup] = useState(false);
  const [abstractDetails, setAbstractDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [btnLoading, setBtnLoading] = useState(false);
  const [openPreviewPopup, setOpenPreviewPopup] = useState(false);
  const [openHallTicketPopup, setOpenHallTicketPopup] = useState(false);
  const [s3URL, setS3URL] = useState('');
  const userType = getUserType();

  const { userDetails } = useSelector((state) => ({
    userDetails: state.student.userDetails,
  }));

  const { user_type } = getDataFromLocalStorage();

  const {
    // view: viewAccess,
    create: createAccess,
  } =
    userDetails?.role_data?.find(
      (item) => item?.permission_name === 'Instant Ticket Generate'
    )?.sub_permission || {};

  const getEventAbstractDetails = async () => {
    const response = await dispatch(fetchAbstractDetails(abstract_id));
    if (response.status === 200) {
      console.log('✌️response --->', response);
      const data = response?.data || {};
      setAbstractDetails(data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getEventAbstractDetails();
  }, [abstract_id]);

  const initialValues = {
    payment_category: abstractDetails?.payment_category || '',
    reference_no: abstractDetails?.reference_no || '',
    payment_type: paymentMethod
      ? paymentMethod
      : abstractDetails?.payment_type || '',
    currency_type: abstractDetails?.currency_type || '',
    total_amount: abstractDetails?.total_amount || '',
    collected_by: abstractDetails?.collector || '',
    verified_by: abstractDetails?.verifier || '',
    comment: '',
    preferences: {
      whatsapp: false,
      email: false,
    },
  };

  const validationSchema = Yup.object().shape({
    payment_category: Yup.string().required('Category is required'),
    reference_no: Yup.string().required('Reference No is required'),
    payment_type: Yup.string().required('Payment type is required'),
    currency_type: Yup.string().required('Currency type is required'),
    total_amount: Yup.number()
      .typeError('Amount must be a number')
      .required('Total amount is required')
      .positive('Amount must be greater than 0'),
    collected_by: Yup.string().required('Collector is required'),
    verified_by: Yup.string().required('Verifier is required'),
    comment: Yup.string().when([], {
      is: () =>
        !abstractDetails?.is_collector_completed ||
        !abstractDetails?.is_verifier_completed,
      then: Yup.string().required('Comment is required'),
      otherwise: Yup.string(),
    }),
    preferences: Yup.object().when([], {
      is: () =>
        abstractDetails?.am_i_verifier &&
        ['ccavenue', 'paypal'].includes(initialValues.payment_type),
      then: Yup.object()
        .shape({
          whatsapp: Yup.boolean(),
          email: Yup.boolean(),
        })
        .test(
          'at-least-one',
          'At least one preference (WhatsApp or Email) must be selected',
          (value) => value?.whatsapp || value?.email
        ),
      otherwise: Yup.object().shape({
        whatsapp: Yup.boolean(),
        email: Yup.boolean(),
      }),
    }),
  });

  const handleSave = async (values) => {
    setBtnLoading(true);
    const { preferences } = values;
    const sendVia = Object.keys(preferences)
      .filter((key) => preferences[key])
      .join(',');
    const payload = {
      abstract_id: abstract_id,
      event_id: eventId,
      ref_no: values.reference_no,
      payment_category: values.payment_category,
      payment_type: values.payment_type,
      total_amount: values.total_amount,
      collected_by: values.collected_by,
      verified_by: values.verified_by,
      comment: values.comment,
      currency_type: values.currency_type,
      preference: sendVia,
    };
    const response = await dispatch(
      handleActionOnPaper(objectToFormData(payload))
    );
    if (response.status === 200) {
      dispatch(showSuccess(response.message));
      getEventAbstractDetails();
    }
    setBtnLoading(false);
  };

  const fetchProfile = async (profileName) => {
    if (profileName) {
      const response = await generatePreSignedUrl(profileName, profilePath);
      setS3URL(response);
    }
  };

  useEffect(() => {
    if (abstractDetails?.profile_image) {
      fetchProfile(abstractDetails?.profile_image);
    }
  }, [abstractDetails?.profile_image]);

  return (
    <>
      <div id="conferenceTicketsView">
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className="general-detail">
              <div
                className="d-flex pb-1"
                onClick={() => {
                  navigate(-1);
                }}
              >
                <img
                  src={icons.leftArrow}
                  alt="back"
                  className="h-21 me-3 pointer"
                />
              </div>
              <div className="basic-detail">
                <img
                  src={s3URL || icons.profileImg}
                  alt="User Image"
                  className="user-img"
                />
                <div>
                  <h4>{abstractDetails?.user_name}</h4>
                  <div className="contact-info">

                    {abstractDetails?.email_id && <div>
                      <img src={icons.emailIcon} alt="Icon" />
                      <span>{abstractDetails?.email_id || '-'}</span>
                    </div>}
                    {abstractDetails?.phone_number &&
                      <div>
                        <img src={icons.callIcon} alt="Icon" />
                        <span>
                          {abstractDetails?.phone_code || '-'}{' '}
                          {abstractDetails?.phone_number || '-'}
                        </span>
                      </div>}
                    {abstractDetails?.county && (
                      <div>
                        <img src={icons.earthIcon} alt="Icon" />
                        <span>{abstractDetails?.county}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="conference-info">
                <h4>Conference & Abstract Info :</h4>
                <div className="detail">
                  <div>
                    <h5>Conference Name :</h5>
                    <p>{abstractDetails?.event_name || '-'}</p>
                  </div>
                  <div>
                    <h5>Download Status :</h5>
                    <div
                      className={paymentColor(abstractDetails?.download_status)}
                    >
                      {abstractDetails?.download_status
                        ? abstractDetails?.download_status
                        : '-'}
                    </div>
                    {/* <p className="status">{abstractDetails?.download_status}</p> */}
                  </div>
                  <div>
                    <h5>Paper Title :</h5>
                    <p>{abstractDetails?.paper_title || '-'}</p>
                  </div>
                  <div>
                    <h5>Paper ID :</h5>
                    <p>{abstractDetails?.paper_id || '-'}</p>
                  </div>
                  <div>
                    <h5>Presentation Type :</h5>
                    <p>{abstractDetails?.presentation_type || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="row g-3">
              <div className="col-xl-6">
                <div className="other-detail h-100">
                  <h5>Payment Details</h5>

                  {abstractDetails?.is_account_side_payment === "1" ?
                    <AccountantVerifyData abstractDetails={abstractDetails} />
                    :
                    !abstractDetails?.is_payment_section_visible ? (
                      <div className="pay-now-btn">
                        <div className="text-20-400-25 cmb-20">
                          <span className="cme-5">
                            <img src={icons.circleInfo} height={24} width={24} />
                          </span>
                          Make Payment Now
                        </div>

                        {createAccess === '1' && (
                          <div className="d-flex align-items-center">
                            <Button
                              text="Pay Now"
                              btnStyle="primary-dark"
                              className="cps-30 cpe-30 h-35"
                              icon={<i class="bi bi-currency-dollar"></i>}
                              onClick={() => setOpenPaymentPopup(true)}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <Formik
                          initialValues={initialValues}
                          validationSchema={validationSchema}
                          onSubmit={(values) => {
                            handleSave(values);
                          }}
                          enableReinitialize
                        >
                          {(props) => {
                            const {
                              values,
                              errors,
                              handleChange,
                              setFieldValue,
                              handleSubmit,
                              resetForm,
                            } = props;

                            const {
                              payment_category,
                              reference_no,
                              payment_type,
                              currency_type,
                              total_amount,
                              collected_by,
                              verified_by,
                              comment,
                            } = values;

                            return (
                              <form>
                                <div className="row gy-4">
                                  <div className="col-md-6">
                                    <Dropdown
                                      label="Payment Category"
                                      id="payment_category"
                                      placeholder="Select category"
                                      options={paymentCategory}
                                      value={payment_category}
                                      error={errors?.payment_category}
                                      optionValue={'name'}
                                      optionKey={'value'}
                                      onChange={handleChange}
                                      disabled={abstractDetails?.payment_category}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <TextInput
                                      label="Reference No"
                                      placeholder="Enter reference no"
                                      id="reference_no"
                                      onChange={handleChange}
                                      value={reference_no}
                                      error={errors.reference_no}
                                      disabled={abstractDetails?.reference_no}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <Dropdown
                                      label="Payment Type"
                                      id="payment_type"
                                      options={paymentType}
                                      placeholder="Select type"
                                      optionValue="name"
                                      optionKey="value"
                                      value={payment_type}
                                      onChange={handleChange}
                                      disabled={
                                        paymentMethod ||
                                        abstractDetails?.payment_type
                                      }
                                      error={errors.payment_type}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <Dropdown
                                      id="currency_type"
                                      placeholder="Select Currency"
                                      options={currencyType}
                                      value={currency_type}
                                      optionKey="id"
                                      optionValue="label"
                                      label="Currency Type"
                                      onChange={handleChange}
                                      error={errors.currency_type}
                                      disabled={abstractDetails?.currency_type}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <TextInput
                                      label="Total Amount"
                                      placeholder="Enter total amount"
                                      id="total_amount"
                                      onChange={handleChange}
                                      value={total_amount}
                                      error={errors.total_amount}
                                      disabled={abstractDetails?.total_amount}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <Dropdown
                                      label="Collected By"
                                      id="collected_by"
                                      placeholder="Select collected by"
                                      value={collected_by}
                                      error={errors.collected_by}
                                      options={abstractDetails?.collecttors}
                                      optionKey="user_id"
                                      optionValue="name"
                                      onChange={handleChange}
                                      disabled={abstractDetails?.collector}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <Dropdown
                                      label="Verified By"
                                      id="verified_by"
                                      placeholder="Select verified by"
                                      options={abstractDetails?.verifiers}
                                      optionKey="user_id"
                                      optionValue="name"
                                      value={verified_by}
                                      onChange={handleChange}
                                      disabled={abstractDetails?.verifier}
                                      error={errors.verified_by}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <div className="payment-status mt-md-4">
                                      <h6>Payment Status :</h6>

                                      <div
                                        className={paymentColor(
                                          abstractDetails?.payment_status
                                        )}
                                      >
                                        {abstractDetails?.payment_status
                                          ? abstractDetails?.payment_status
                                          : '-'}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div
                                  className={`cmb-18 my-4 br-5 ${coAuthorName ? 'b-0-7-5' : ''
                                    }`}
                                >
                                  <div className="bg-E9EE br-5 text-14-500-17 d-flex flex-column">
                                    <div
                                      className="cps-20 cpt-12 cpe-20 color-new-car cpb-12 d-flex align-items-center justify-content-between pointer flex-grow-1"
                                      onClick={() => {
                                        setCoAuthorName(!coAuthorName);
                                      }}
                                    >
                                      <div>Co-Author Name</div>
                                      <i
                                        className={`${coAuthorName
                                          ? 'bi bi-chevron-up'
                                          : 'bi bi-chevron-down'
                                          }`}
                                        style={{ fontSize: '15px' }}
                                      />
                                    </div>
                                    {coAuthorName &&
                                      abstractDetails?.co_authors?.length > 0 && (
                                        <div className="cp-15 bg-white br-5 iferp-scroll overflow-auto">
                                          <ol className="co-author-name">
                                            {abstractDetails?.co_authors?.map(
                                              (author, index) => (
                                                <li key={author.id || index}>
                                                  {author.name}
                                                </li>
                                              )
                                            )}
                                          </ol>
                                        </div>
                                      )}
                                  </div>
                                </div>
                                {/* Comment Field */}
                                {!abstractDetails?.is_collector_completed && ["6"].includes(user_type) &&
                                  !abstractDetails?.is_verifier_completed && (
                                    <div className="col-md-6">
                                      <TextArea
                                        label="Comment"
                                        placeholder="Enter comment"
                                        id="comment"
                                        value={comment}
                                        error={errors.comment}
                                        onChange={handleChange}
                                        rows={3}
                                      />
                                    </div>
                                  )}

                                {abstractDetails?.is_collector_completed && (
                                  <div className="comment-detail">
                                    <h6>
                                      Comment ({abstractDetails?.collector_name})
                                    </h6>
                                    <p>{abstractDetails?.collector_comment}</p>
                                  </div>
                                )}

                                {abstractDetails?.am_i_verifier &&
                                  !abstractDetails?.is_verifier_completed && ["6"].includes(user_type) && (
                                    <div className="col-md-6">
                                      <TextArea
                                        label="Comment"
                                        placeholder="Enter comment"
                                        id="comment"
                                        value={comment}
                                        error={errors.comment}
                                        onChange={handleChange}
                                        rows={3}
                                      />
                                    </div>
                                  )}

                                {abstractDetails?.is_verifier_completed && (
                                  <div className="comment-detail">
                                    <h6>Comment</h6>
                                    <p>{abstractDetails?.verifier_comment}</p>
                                  </div>
                                )}

                                {abstractDetails?.am_i_verifier &&
                                  !abstractDetails?.is_verifier_completed && ["6"].includes(user_type) &&
                                  ['ccavenue', 'paypal'].includes(
                                    payment_type
                                  ) && (
                                    <>
                                      <div className="d-flex gap-3 cmt-5">
                                        <div className="d-flex align-items-center gap-2">
                                          <CheckBox
                                            type="ACTIVE"
                                            isChecked={
                                              values.preferences.whatsapp
                                            }
                                            onClick={() =>
                                              setFieldValue(
                                                'preferences.whatsapp',
                                                !values.preferences.whatsapp
                                              )
                                            }
                                          />
                                          <span className="text-black-olive text-14-300-18">
                                            WhatsApp
                                          </span>
                                        </div>

                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                          <CheckBox
                                            type="ACTIVE"
                                            isChecked={values.preferences.email}
                                            onClick={() =>
                                              setFieldValue(
                                                'preferences.email',
                                                !values.preferences.email
                                              )
                                            }
                                          />
                                          <span className="text-black-olive text-14-300-18">
                                            Email
                                          </span>
                                        </div>
                                      </div>

                                      {errors.preferences &&
                                        typeof errors.preferences === 'string' &&
                                        abstractDetails?.am_i_verifier &&
                                        ['ccavenue', 'paypal'].includes(
                                          payment_type
                                        ) && (
                                          <div className="text-danger">
                                            {errors.preferences}
                                          </div>
                                        )}
                                    </>
                                  )}
                                {/* Action Buttons */}
                                {["6"].includes(user_type) && ((abstractDetails?.am_i_collecttor &&
                                  !abstractDetails?.is_collector_completed) ||
                                  (abstractDetails?.am_i_verifier &&
                                    !abstractDetails?.is_verifier_completed)) ? (
                                  <div className="d-flex gap-4 cmt-15">
                                    {createAccess === '1' && (
                                      <Button
                                        text={
                                          ['ccavenue', 'paypal'].includes(
                                            payment_type
                                          )
                                            ? abstractDetails?.am_i_collecttor
                                              ? 'Verify'
                                              : abstractDetails?.am_i_verifier
                                                ? 'Confirm & Send Link'
                                                : ''
                                            : abstractDetails?.am_i_collecttor
                                              ? 'Send Verification'
                                              : abstractDetails?.am_i_verifier
                                                ? 'Confirm Verify'
                                                : ''
                                        }
                                        btnStyle="primary-dark"
                                        className="h-35 cps-40 cpe-40"
                                        onClick={handleSubmit}
                                        btnLoading={btnLoading}
                                        disabled={btnLoading}
                                      />
                                    )}
                                    <Button
                                      text="Cancel"
                                      btnStyle="primary-gray"
                                      className="h-35 cps-50 cpe-50"
                                      disabled={btnLoading}
                                      onClick={() =>
                                        navigate(
                                          `/${userType}/instant-ticket-generate/${eventId}/client-conference-tickets`
                                        )
                                      }
                                    />
                                  </div>
                                ) : null}
                              </form>
                            );
                          }}
                        </Formik>
                      </>
                    )}
                </div>
              </div>
              <div className="col-xl-6">
                <Receipt
                  abstractDetails={abstractDetails}
                  setOpenPreviewPopup={setOpenPreviewPopup}
                  type="Receipt"
                />
                <Receipt
                  abstractDetails={abstractDetails}
                  setOpenHallTicketPopup={setOpenHallTicketPopup}
                  type="Hall Ticket"
                />
              </div>
            </div>
          </>
        )}
      </div>

      {openPaymentPopup && (
        <PaymentPopup
          onHide={() => setOpenPaymentPopup(false)}
          setPaymentMethod={setPaymentMethod}
          setAbstractDetails={setAbstractDetails}
          abstractDetails={abstractDetails}
        />
      )}

      {openPreviewPopup && (
        <PreviewReceiptPopup
          onHide={() => setOpenPreviewPopup(false)}
          abstractDetails={abstractDetails}
        />
      )}

      {openHallTicketPopup && (
        <PreviewHallTicketPopup
          onHide={() => setOpenHallTicketPopup(false)}
          pdfUrl={abstractDetails.hall_ticket_url}
        />
      )}
    </>
  );
};

export default ClientConferenceTicketsView;

{
  /* <div className="other-detail hall-ticket-div">
<h5>Hall Ticket</h5>
{!abstractDetails?.is_payment_section_visible ? (
  <div className="flex flex-col items-center justify-center text-center">
    <div className="mb-3">
      <img src={icons.circleInfo} height={24} width={24} />
    </div>
    <div className="text-20-400-25 mb-5">
      Receipt Not Available
    </div>
  </div>
) : (
  <>
    <div className="row">
      <div className="col-md-6">
        <div className=" justify-content-start">
          <div className="text-14-400-18 cmb-14 ">
            Pass Send Via :
          </div>
          <div className="d-flex gap-3">
            <div className="d-flex align-items-center gap-2">
              <CheckBox
                type="ACTIVE"
              
              />

              <span className="text-black-olive text-14-300-18">
                Whats App
              </span>
            </div>
            <div className="d-flex align-items-center justify-content-center gap-2">
              <CheckBox
                type="ACTIVE"
               
              />

              <span className="text-black-olive text-14-300-18">
                E-Mail
              </span>
            </div>
          </div>
         
        </div>
      </div>
      <div className="col-md-6">
        <div className="text-14-400-18 cmb-14 ">
          Hall Ticket :
        </div>
        <button className="preview-btn">
          <img src={icons.blueEyeIcon} alt="Icon" />
          Preview Receipt
        </button>
      </div>
    </div>
    <div className="d-flex align-items-center gap-2 cmb-20">
      <Button
        text="Send"
        btnStyle="primary-dark"
        className="h-35 cps-20 cpe-20 mt-3"
       
      />

      <Button
        text="Print"
        btnStyle="primary-outline"
        className="h-35 cps-20 cpe-20 mt-3"
        
      />
    </div>
  </>
)}
</div> */
}
